import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../supabase";
import { C, font, mono } from "../config/theme";

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE PARAMÈTRES — Règles bv_kpi_rules

   Architecture MCD v14 :
   - Source unique : table bv_kpi_rules (un row par règle, isolé par tenant via RLS)
   - Pas de RPC : SELECT/UPDATE/INSERT directs (RLS authentifie le tenant)
   - Sauvegarde par règle (UPDATE eq.id) — pas de batch

   Catégories logiques calculées côté UI (la table n'a pas de colonne catégorie).
   Onglets pour regrouper visuellement les catégories.
   ═══════════════════════════════════════════════════════════════════════════════ */

/* ─── Catégorisation des règles par préfixe / code connu ─── */
const CATEGORIES = {
  general: {
    label: "Général",
    description: "Paramètres généraux du tenant",
    codes: ["devise", "exercice_debut_mois"],
  },
  formules: {
    label: "Formules de calcul",
    description: "Définitions des calculs métier",
    codes: ["formule_raf", "methode_avancement"],
  },
  seuils: {
    label: "Seuils d'alerte",
    description: "Seuils déclencheurs de la détection automatique",
    codes: ["seuil_marge_alerte", "seuil_mo_depassement", "seuil_sous_facturation"],
  },
  modules: {
    label: "Modules activés",
    description: "Activation des fonctionnalités optionnelles",
    codes: ["activation_projection"],
  },
  autre: {
    label: "Autres règles",
    description: "Règles personnalisées hors catégories standard",
    codes: [],
  },
};

/* ─── Onglets regroupant les catégories ─── */
const TABS = [
  { id: "general",  label: "Général",            cats: ["general", "modules"] },
  { id: "calculs",  label: "Calculs & alertes",  cats: ["formules", "seuils"] },
  { id: "autre",    label: "Autres",             cats: ["autre"] },
];

/* ─── Helper : déterminer la catégorie d'une règle à partir de son code ─── */
function categoryOf(code) {
  for (const [key, cat] of Object.entries(CATEGORIES)) {
    if (cat.codes.includes(code)) return key;
  }
  return "autre";
}

/* ─── Helper : convertir une string en bool (storage = text "true"/"false") ─── */
const toBool = (v) => v === "true" || v === true;

/* ─── Composant Toggle switch ─── */
function Toggle({ value, onChange, disabled = false }) {
  return (
    <div
      onClick={() => !disabled && onChange(!value)}
      style={{
        width: 42, height: 22, borderRadius: 12,
        background: value ? C.green : C.slateLight,
        padding: 2, boxSizing: "border-box",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        position: "relative", transition: "background 150ms",
        flexShrink: 0,
      }}
      role="switch"
      aria-checked={value}
    >
      <div style={{
        width: 18, height: 18, borderRadius: "50%", background: "#fff",
        boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        transform: `translateX(${value ? 20 : 0}px)`,
        transition: "transform 150ms",
      }} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════════════════════ */
export default function PageParametres({ tenantId, role, onConfigSaved }) {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState("general");

  /* Édition inline ─ id en cours d'édition + valeur temporaire */
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");

  /* Modale ajout règle */
  const [showAddModal, setShowAddModal] = useState(false);

  const readOnly = role === "user";
  const canAdd = role === "admin" || role === "dirigeant";

  /* ─── Chargement des règles ─── */
  const loadRules = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("bv_kpi_rules")
      .select("*")
      .eq("actif", true)
      .order("code_regle");

    if (error) {
      console.error("Erreur chargement bv_kpi_rules:", error);
      setFeedback({ type: "error", message: "Erreur de chargement des règles." });
      setLoading(false);
      return;
    }
    setRules(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  /* ─── Feedback auto-dismiss ─── */
  const feedbackTimerRef = useRef(null);
  useEffect(() => {
    if (!feedback) return;
    clearTimeout(feedbackTimerRef.current);
    feedbackTimerRef.current = setTimeout(() => setFeedback(null), 3000);
    return () => clearTimeout(feedbackTimerRef.current);
  }, [feedback]);

  /* ─── Règles groupées par catégorie pour l'onglet actif ─── */
  const groupedByCategory = useMemo(() => {
    const groups = {};
    rules.forEach((r) => {
      const cat = categoryOf(r.code_regle);
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(r);
    });
    return groups;
  }, [rules]);

  const visibleCategories = useMemo(() => {
    const tab = TABS.find((t) => t.id === activeTab);
    if (!tab) return [];
    return tab.cats.filter((c) => (groupedByCategory[c] || []).length > 0);
  }, [groupedByCategory, activeTab]);

  /* ─── Sauvegarde d'une valeur (per-row) ─── */
  const saveValue = useCallback(async (rule, newValue) => {
    if (newValue === rule.valeur) return; // pas de changement
    setSaving(true);
    const { error } = await supabase
      .from("bv_kpi_rules")
      .update({ valeur: newValue })
      .eq("id", rule.id);

    if (error) {
      console.error("Erreur sauvegarde règle:", error);
      setFeedback({ type: "error", message: `Échec de la sauvegarde : ${error.message}` });
    } else {
      setRules((prev) => prev.map((r) => r.id === rule.id ? { ...r, valeur: newValue } : r));
      setFeedback({ type: "success", message: `« ${rule.libelle} » enregistré.` });
      if (onConfigSaved) await onConfigSaved();
    }
    setSaving(false);
  }, [onConfigSaved]);

  /* ─── Édition inline : start / commit / cancel ─── */
  const startEdit = useCallback((rule) => {
    if (readOnly) return;
    setEditingId(rule.id);
    setEditValue(rule.valeur ?? "");
  }, [readOnly]);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue("");
  }, []);

  const commitEdit = useCallback(async (rule) => {
    const v = editValue.trim();
    /* Validation type_valeur */
    if (rule.type_valeur === "numeric" && v !== "" && !/^-?\d+(\.\d+)?$/.test(v)) {
      setFeedback({ type: "error", message: "Valeur numérique invalide." });
      return;
    }
    if (rule.type_valeur === "json" && v !== "") {
      try { JSON.parse(v); } catch {
        setFeedback({ type: "error", message: "JSON invalide." });
        return;
      }
    }
    setEditingId(null);
    await saveValue(rule, v);
  }, [editValue, saveValue]);

  /* ─── Toggle boolean (sauvegarde immédiate) ─── */
  const toggleBool = useCallback(async (rule) => {
    if (readOnly) return;
    const next = toBool(rule.valeur) ? "false" : "true";
    await saveValue(rule, next);
  }, [readOnly, saveValue]);

  /* ─── Ajout d'une nouvelle règle ─── */
  const handleCreateRule = useCallback(async (form) => {
    setSaving(true);
    const { data, error } = await supabase
      .from("bv_kpi_rules")
      .insert({
        tenant_id: tenantId,
        code_regle: form.code_regle.trim(),
        libelle: form.libelle.trim(),
        valeur: form.valeur,
        type_valeur: form.type_valeur,
        description: form.description.trim() || null,
        actif: true,
      })
      .select()
      .single();

    if (error) {
      console.error("Erreur création règle:", error);
      setFeedback({ type: "error", message: `Échec de la création : ${error.message}` });
      setSaving(false);
      return false;
    }
    setRules((prev) => [...prev, data].sort((a, b) => a.code_regle.localeCompare(b.code_regle)));
    setFeedback({ type: "success", message: `Règle « ${data.libelle} » créée.` });
    setShowAddModal(false);
    if (onConfigSaved) await onConfigSaved();
    setSaving(false);
    return true;
  }, [tenantId, onConfigSaved]);

  /* ═══════════════════════════════════════════════════════════════════════════
     RENDU
     ═══════════════════════════════════════════════════════════════════════════ */

  if (loading) {
    return (
      <div style={{ padding: 40, color: C.slate, fontSize: 14 }}>
        Chargement des règles de configuration…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 880 }}>
      {/* ─── En-tête ─── */}
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.navy, margin: 0 }}>Paramètres</h1>
          <p style={{ fontSize: 13, color: C.slate, marginTop: 4, marginBottom: 0 }}>
            Règles métier, seuils d'alerte et modules — pilotage de votre tenant.
          </p>
        </div>
        {canAdd && (
          <button
            onClick={() => setShowAddModal(true)}
            style={{
              padding: "9px 16px", borderRadius: 8, border: "none", background: C.accent,
              color: "#fff", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: font,
              boxShadow: "0 2px 6px rgba(0,0,0,0.12)", whiteSpace: "nowrap",
            }}
          >
            + Ajouter une règle
          </button>
        )}
      </div>

      {/* ─── Onglets ─── */}
      <div style={{ display: "flex", gap: 0, borderBottom: `2px solid ${C.border}`, marginBottom: 20 }}>
        {TABS.map((tab) => {
          const hasRules = tab.cats.some((c) => (groupedByCategory[c] || []).length > 0);
          if (!hasRules) return null;
          const isActive = activeTab === tab.id;
          return (
            <div
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: "10px 20px", fontSize: 13, fontWeight: isActive ? 700 : 500,
                color: isActive ? C.navy : C.slate,
                borderBottom: isActive ? `2px solid ${C.accent}` : "2px solid transparent",
                marginBottom: -2, cursor: "pointer", transition: "all 150ms",
              }}
            >
              {tab.label}
            </div>
          );
        })}
      </div>

      {/* ─── Bandeau lecture seule ─── */}
      {readOnly && (
        <div style={{
          padding: "10px 16px", background: "#FEF3C7", borderRadius: 8,
          fontSize: 13, color: "#92400E", marginBottom: 16,
        }}>
          Vous êtes en lecture seule. Contactez votre dirigeant pour modifier les règles.
        </div>
      )}

      {/* ─── Feedback ─── */}
      {feedback && (
        <div style={{
          padding: "10px 16px",
          background: feedback.type === "success" ? "#D1FAE5" : "#FEE2E2",
          borderRadius: 8, fontSize: 13,
          color: feedback.type === "success" ? "#065F46" : "#991B1B",
          marginBottom: 16,
        }}>
          {feedback.message}
        </div>
      )}

      {/* ─── Cartes par catégorie ─── */}
      {visibleCategories.length === 0 && (
        <div style={{
          padding: "40px 24px", textAlign: "center", color: C.slate, fontSize: 13,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12,
        }}>
          Aucune règle dans cet onglet.
        </div>
      )}

      {visibleCategories.map((catKey) => {
        const cat = CATEGORIES[catKey];
        const rulesInCat = groupedByCategory[catKey] || [];
        return (
          <div
            key={catKey}
            style={{
              background: C.surface, borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
              marginBottom: 20, overflow: "hidden", border: `1px solid ${C.border}`,
            }}
          >
            {/* En-tête catégorie */}
            <div style={{ padding: "14px 20px", borderBottom: `1px solid ${C.border}`, background: "#FAFBFC" }}>
              <h2 style={{
                fontSize: 13, fontWeight: 700, color: C.navy, margin: 0,
                textTransform: "uppercase", letterSpacing: "0.5px",
              }}>
                {cat.label}
              </h2>
              {cat.description && (
                <p style={{ fontSize: 11, color: C.slate, margin: "3px 0 0 0" }}>
                  {cat.description}
                </p>
              )}
            </div>

            {/* Liste des règles */}
            <div>
              {rulesInCat.map((rule, idx) => (
                <RuleRow
                  key={rule.id}
                  rule={rule}
                  isLast={idx === rulesInCat.length - 1}
                  readOnly={readOnly}
                  saving={saving}
                  isEditing={editingId === rule.id}
                  editValue={editValue}
                  setEditValue={setEditValue}
                  startEdit={startEdit}
                  commitEdit={commitEdit}
                  cancelEdit={cancelEdit}
                  toggleBool={toggleBool}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* ─── Modale ajout ─── */}
      {showAddModal && (
        <AddRuleModal
          onClose={() => setShowAddModal(false)}
          onCreate={handleCreateRule}
          saving={saving}
          existingCodes={rules.map((r) => r.code_regle)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   RuleRow — Ligne d'une règle (édition inline)
   ═══════════════════════════════════════════════════════════════════════════════ */
function RuleRow({
  rule, isLast, readOnly, saving,
  isEditing, editValue, setEditValue, startEdit, commitEdit, cancelEdit, toggleBool,
}) {
  const isBool = rule.type_valeur === "boolean";

  return (
    <div style={{
      padding: "14px 20px",
      borderBottom: isLast ? "none" : `1px solid ${C.border}`,
      display: "flex", alignItems: "center", gap: 16,
      background: isEditing ? "#FFFBEB" : "transparent",
      transition: "background 150ms",
    }}>
      {/* Libellé + description + code */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {rule.libelle}
          </label>
          <code style={{ fontSize: 10, color: C.slateLight, fontFamily: mono }}>
            {rule.code_regle}
          </code>
          <span style={{
            fontSize: 9, fontWeight: 700, textTransform: "uppercase",
            padding: "1px 6px", borderRadius: 999,
            background: C.bg, color: C.slate, letterSpacing: "0.5px",
          }}>
            {rule.type_valeur}
          </span>
        </div>
        {rule.description && (
          <div style={{ fontSize: 11, color: C.slate, marginTop: 3, lineHeight: 1.4 }}>
            {rule.description}
          </div>
        )}
      </div>

      {/* Valeur — éditable */}
      <div style={{ flexShrink: 0, width: 240, display: "flex", justifyContent: "flex-end" }}>
        {isBool ? (
          <Toggle value={toBool(rule.valeur)} onChange={() => toggleBool(rule)} disabled={readOnly || saving} />
        ) : isEditing ? (
          <input
            type={rule.type_valeur === "numeric" ? "number" : "text"}
            value={editValue}
            autoFocus
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={() => commitEdit(rule)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.target.blur(); }
              if (e.key === "Escape") { cancelEdit(); }
            }}
            disabled={saving}
            style={{
              width: "100%", padding: "7px 10px", fontSize: 13,
              fontFamily: rule.type_valeur === "numeric" ? mono : font,
              border: `1px solid ${C.accent}`, borderRadius: 6,
              background: "#fff", color: C.text, outline: "none",
            }}
          />
        ) : (
          <div
            onClick={() => startEdit(rule)}
            title={readOnly ? "Lecture seule" : "Cliquer pour modifier"}
            style={{
              width: "100%", padding: "7px 10px", fontSize: 13,
              fontFamily: ["numeric", "json"].includes(rule.type_valeur) ? mono : font,
              border: `1px dashed ${readOnly ? "transparent" : C.border}`,
              borderRadius: 6,
              background: readOnly ? "transparent" : C.bg,
              color: C.text,
              cursor: readOnly ? "default" : "text",
              textAlign: "right",
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
          >
            {rule.valeur || <span style={{ color: C.slateLight }}>—</span>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════════
   AddRuleModal — Création d'une nouvelle règle
   ═══════════════════════════════════════════════════════════════════════════════ */
function AddRuleModal({ onClose, onCreate, saving, existingCodes }) {
  const [form, setForm] = useState({
    code_regle: "",
    libelle: "",
    valeur: "",
    type_valeur: "text",
    description: "",
  });
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    const code = form.code_regle.trim();
    /* Validation */
    if (!code) return setError("Code requis.");
    if (!/^[a-z][a-z0-9_]*$/.test(code)) {
      return setError("Code : minuscules, chiffres et _ uniquement, doit commencer par une lettre.");
    }
    if (existingCodes.includes(code)) return setError("Ce code existe déjà.");
    if (!form.libelle.trim()) return setError("Libellé requis.");
    if (form.type_valeur === "numeric" && form.valeur && !/^-?\d+(\.\d+)?$/.test(form.valeur.trim())) {
      return setError("Valeur numérique invalide.");
    }
    if (form.type_valeur === "json" && form.valeur) {
      try { JSON.parse(form.valeur); } catch { return setError("JSON invalide."); }
    }
    if (form.type_valeur === "boolean" && !["true", "false"].includes(form.valeur)) {
      return setError("Valeur booléenne : « true » ou « false ».");
    }
    setError(null);
    await onCreate({ ...form, code_regle: code });
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 12, padding: "24px 28px",
          width: 480, maxWidth: "calc(100vw - 40px)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.navy }}>
          Ajouter une règle
        </h2>
        <p style={{ fontSize: 12, color: C.slate, margin: "4px 0 18px" }}>
          Les codes en minuscules avec underscores (ex: <code style={{ fontFamily: mono }}>seuil_xxx</code>).
        </p>

        <FormField label="Code" hint="Identifiant technique, immuable">
          <input
            type="text"
            value={form.code_regle}
            onChange={(e) => setForm({ ...form, code_regle: e.target.value })}
            placeholder="ex: seuil_dso"
            style={inputStyle}
          />
        </FormField>

        <FormField label="Libellé" hint="Affiché dans l'interface">
          <input
            type="text"
            value={form.libelle}
            onChange={(e) => setForm({ ...form, libelle: e.target.value })}
            placeholder="ex: Seuil alerte DSO"
            style={inputStyle}
          />
        </FormField>

        <FormField label="Type de valeur">
          <select
            value={form.type_valeur}
            onChange={(e) => setForm({ ...form, type_valeur: e.target.value, valeur: e.target.value === "boolean" ? "false" : "" })}
            style={inputStyle}
          >
            <option value="text">Texte</option>
            <option value="numeric">Numérique</option>
            <option value="boolean">Booléen</option>
            <option value="json">JSON</option>
          </select>
        </FormField>

        <FormField label="Valeur initiale">
          {form.type_valeur === "boolean" ? (
            <select
              value={form.valeur || "false"}
              onChange={(e) => setForm({ ...form, valeur: e.target.value })}
              style={inputStyle}
            >
              <option value="true">true (activé)</option>
              <option value="false">false (désactivé)</option>
            </select>
          ) : (
            <input
              type={form.type_valeur === "numeric" ? "number" : "text"}
              value={form.valeur}
              onChange={(e) => setForm({ ...form, valeur: e.target.value })}
              placeholder={form.type_valeur === "json" ? '{"key":"value"}' : ""}
              style={inputStyle}
            />
          )}
        </FormField>

        <FormField label="Description" hint="Aide contextuelle affichée sous le libellé">
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            style={{ ...inputStyle, resize: "vertical", fontFamily: font }}
          />
        </FormField>

        {error && (
          <div style={{
            padding: "8px 12px", background: "#FEE2E2", color: "#991B1B",
            borderRadius: 6, fontSize: 12, marginBottom: 12,
          }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "8px 16px", borderRadius: 8, border: `1px solid ${C.border}`,
              background: "#fff", color: C.slate, fontSize: 13, fontWeight: 500,
              cursor: "pointer", fontFamily: font,
            }}
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none",
              background: saving ? C.slateLight : C.navy, color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: saving ? "wait" : "pointer", fontFamily: font,
            }}
          >
            {saving ? "Création…" : "Créer la règle"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "8px 10px", fontSize: 13,
  border: `1px solid ${C.border}`, borderRadius: 6,
  fontFamily: font, color: C.text, outline: "none", boxSizing: "border-box",
};

function FormField({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>
        {label}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 11, color: C.slate, marginTop: 3 }}>{hint}</div>
      )}
    </div>
  );
}
